import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

export const MAX_MESSAGE_BODY_LENGTH = 4000;

interface MessageComposerSignature {
  Args: {
    recipientFirstName: string;
    inputId?: string;
    isSending?: boolean;
    errorMessage?: string | null;
    onSend: (body: string) => void | Promise<void>;
  };
  Element: HTMLDivElement;
}

export default class MessageComposer extends Component<MessageComposerSignature> {
  @tracked draft = '';

  get fieldId(): string {
    return this.args.inputId ?? 'messageDraft';
  }

  get canSend(): boolean {
    const body = this.draft.trim();
    return (
      body.length > 0 &&
      body.length <= MAX_MESSAGE_BODY_LENGTH &&
      !this.args.isSending
    );
  }

  get isSendDisabled(): boolean {
    return !this.canSend;
  }

  updateDraft = (event: Event) => {
    this.draft = (event.target as HTMLInputElement).value;
  };

  submit = async (event?: Event) => {
    event?.preventDefault();
    if (!this.canSend) {
      return;
    }

    const body = this.draft.trim();
    try {
      await this.args.onSend(body);
      this.draft = '';
    } catch {
      // The conversation shows the send error; keep the draft.
    }
  };

  onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      void this.submit(event);
    }
  };

  <template>
    <div
      class="border-top px-3 py-3 d-flex flex-column gap-2 flex-shrink-0"
      ...attributes
    >
      {{#if @errorMessage}}
        <p class="small text-danger mb-0" role="alert">{{@errorMessage}}</p>
      {{/if}}

      <form class="d-flex align-items-center gap-3" {{on "submit" this.submit}}>
        <div
          class="flex-grow-1 bg-body-secondary rounded-pill ps-4 pe-2 d-flex align-items-center gap-2 coaching-messages-composer-field"
        >
          <label class="visually-hidden" for={{this.fieldId}}>Message
            {{@recipientFirstName}}</label>
          <input
            id={{this.fieldId}}
            type="text"
            class="form-control bg-transparent border-0 shadow-none py-2 flex-grow-1 min-w-0"
            placeholder="Aa"
            maxlength={{MAX_MESSAGE_BODY_LENGTH}}
            value={{this.draft}}
            disabled={{@isSending}}
            data-test-message-draft
            {{on "input" this.updateDraft}}
            {{on "keydown" this.onKeydown}}
          />
          <button
            type="submit"
            class="btn rounded-circle p-0 d-inline-flex align-items-center justify-content-center flex-shrink-0 coaching-messages-composer-send-btn
              {{if this.canSend 'btn-primary' 'border border-primary'}}"
            aria-label="Send message"
            disabled={{this.isSendDisabled}}
            data-test-message-send
          >
            <iconify-icon
              inline
              icon="ph:paper-plane-tilt"
              class="fs-6 {{unless this.canSend 'text-primary'}}"
              aria-hidden="true"
            />
          </button>
        </div>
      </form>
    </div>
  </template>
}
